import { useCallback, type ReactNode } from 'react';
import { App } from 'antd';
import { errorMessage } from './errorMessage';

/**
 * One confirmation idiom for every destructive or notable action.
 *
 * Replaces 15 hand-rolled `Modal.confirm` blocks and 62 repetitions of
 * `(err as { message?: string }).message ?? '…failed'`. It also fixes two real
 * inconsistencies those copies had drifted into: a Popconfirm whose confirm
 * button rendered non-danger under a danger trigger, and a portal Restore with
 * no confirmation where the console's had one.
 *
 * Uses antd's App context (`<App>`) rather than the static `Modal.confirm`, so
 * dialogs inherit the configured theme instead of rendering outside it.
 */
export interface ConfirmActionOptions {
  title: string;
  content?: ReactNode;
  okText: string;
  danger?: boolean;
  onConfirm: () => Promise<void>;
  /** Shown via `message.success` after onConfirm resolves. */
  successMessage?: string;
  /**
   * 'keep-open' (default) rethrows so antd keeps the dialog open and the
   * operator can retry — matching the existing list-page idiom.
   */
  onFailure?: 'keep-open' | 'close';
}

export function useConfirmAction(): (opts: ConfirmActionOptions) => void {
  const { modal, message } = App.useApp();

  return useCallback(
    (opts: ConfirmActionOptions) => {
      const instance = modal.confirm({
        title: opts.title,
        content: opts.content,
        okText: opts.okText,
        okButtonProps: opts.danger ? { danger: true } : undefined,
        onOk: async () => {
          try {
            await opts.onConfirm();
            if (opts.successMessage) message.success(opts.successMessage);
          } catch (err: unknown) {
            message.error(errorMessage(err, `${opts.okText} failed`));
            if (opts.onFailure !== 'close') throw err;
          }
        },
      });
      // The hook-based modal.confirm() returned instance is thenable, and
      // consuming it flips antd's internal `isSilent` flag. Without this,
      // rethrowing inside onOk (the 'keep-open' path above) additionally
      // surfaces as an unhandled promise rejection (antd's ActionButton
      // deliberately re-throws unless the caller consumes the instance —
      // see https://github.com/ant-design/ant-design/issues/6183). We
      // already report the failure via message.error above, so swallow it
      // here too.
      instance.then(
        () => undefined,
        () => undefined,
      );
    },
    [modal, message],
  );
}
